import type { ReactNode } from "react";
import { GlassPanel } from "./GlassPanel";
import { CtaLink } from "./Button";
import "./PricingCard.css";

export interface PricingTier {
  id: string;
  name: string;
  /** A short label, not a number — see docs/DESIGN_ASSESSMENT_2026-07.md §10-11 (R1). */
  price: string;
  /**
   * Owner-approved monetary tiers only (2026-08-17): the annual price plus
   * the truthful "2 months free" framing, shown directly under `price`.
   * Omitted for Creator (free) and Enterprise (Contact Sales, no fixed
   * price) — those two never had a real number to show here.
   */
  annualPrice?: string;
  purpose: string;
  /** The one-sentence commercial philosophy for this plan — "Everything required to…". */
  promise: string;
  audience: string;
  /** What this tier actually includes in V1 today -- only real, server-enforced entitlements. */
  scope: string[];
  /** What this tier explicitly does not include. Shown plainly, not in a tooltip. */
  notIncluded?: string[];
  /**
   * Direction Devory is building toward, not yet part of V1 -- rendered
   * under its own unmistakable "Coming later" heading, never mixed into
   * `scope`. A customer must never reasonably read this as something they
   * receive by purchasing today.
   */
  roadmap?: string[];
  /** Why someone outgrows this tier and moves to the next one. Omitted on the last tier. */
  nextStep?: string;
  availability: string;
  cta: { label: string; to: string };
  /**
   * Exactly one tier may be marked recommended — Professional, the primary
   * self-serve recommendation for most visitors. This is a real product
   * decision (see DEVORY_PRICING_ARCHITECTURE_2026-07.md), not a "Most
   * Popular" claim with no data behind it — the label says what it is.
   */
  recommended?: boolean;
}

/**
 * All four tiers share the same card structure and the same visual weight;
 * only the `recommended` tier gets a border/label treatment, built entirely
 * from existing accent tokens — no new colour, shape or animation introduced.
 */
export function PricingCard({ tier, footnote }: { tier: PricingTier; footnote?: ReactNode }) {
  return (
    <GlassPanel
      as="article"
      className={tier.recommended ? "pricing-card pricing-card-recommended" : "pricing-card"}
    >
      {tier.recommended ? <p className="pricing-card-badge">Recommended</p> : null}
      <h3>{tier.name}</h3>
      <p className="pricing-card-price">{tier.price}</p>
      {tier.annualPrice ? <p className="pricing-card-annual-price">{tier.annualPrice}</p> : null}
      <p className="pricing-card-purpose">{tier.purpose}</p>
      <p className="pricing-card-promise">{tier.promise}</p>
      <p className="pricing-card-audience">{tier.audience}</p>
      <ul>
        {tier.scope.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {tier.notIncluded ? (
        <ul className="pricing-card-excluded">
          {tier.notIncluded.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {tier.roadmap ? (
        <div className="pricing-card-roadmap">
          <p className="pricing-card-roadmap-heading">Coming later, not included yet</p>
          <ul>
            {tier.roadmap.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="pricing-card-availability">{tier.availability}</p>
      <CtaLink to={tier.cta.to} variant={tier.recommended ? "primary" : "secondary"} block>
        {tier.cta.label}
      </CtaLink>
      {tier.nextStep ? <p className="pricing-card-next">{tier.nextStep}</p> : null}
      {footnote ? <p className="pricing-card-footnote">{footnote}</p> : null}
    </GlassPanel>
  );
}
